/**
 * Puerto de almacenamiento
 *
 * Abstrae el almacenamiento local para permitir diferentes implementaciones:
 * - Web: localStorage/sessionStorage
 * - Mobile: Capacitor Preferences / Secure Storage
 */
export interface StoragePort {
  /**
   * Obtiene un valor del almacenamiento
   */
  get<T = string>(key: string): Promise<T | null>

  /**
   * Guarda un valor en el almacenamiento
   */
  set<T = string>(key: string, value: T): Promise<void>

  /**
   * Elimina un valor del almacenamiento
   */
  remove(key: string): Promise<void>

  /**
   * Limpia todo el almacenamiento
   */
  clear(): Promise<void>

  /**
   * Obtiene todas las claves almacenadas
   */
  keys(): Promise<string[]>
}

/**
 * Puerto de almacenamiento seguro para datos sensibles
 *
 * Implementaciones:
 * - Web: Cifrado en memoria + localStorage
 * - Mobile: iOS Keychain / Android Keystore
 */
export interface SecureStoragePort {
  /**
   * Almacena un valor de forma segura
   */
  setSecure(key: string, value: string): Promise<void>

  /**
   * Obtiene un valor seguro
   */
  getSecure(key: string): Promise<string | null>

  /**
   * Elimina un valor seguro
   */
  removeSecure(key: string): Promise<void>

  /**
   * Limpia todos los valores seguros
   */
  clearSecure(): Promise<void>
}

// ============== REGISTRO GLOBAL ==============

let storageInstance: StoragePort | null = null
let secureStorageInstance: SecureStoragePort | null = null

/**
 * Registra la implementación de StoragePort
 */
export function registerStorage(storage: StoragePort): void {
  storageInstance = storage
}

/**
 * Obtiene la instancia registrada de StoragePort
 */
export function getStorage(): StoragePort {
  if (!storageInstance) {
    throw new Error('StoragePort no registrado. Llama a registerStorage() primero.')
  }
  return storageInstance
}

/**
 * Verifica si hay un StoragePort registrado
 */
export function isStorageRegistered(): boolean {
  return storageInstance !== null
}

/**
 * Registra la implementación de SecureStoragePort
 */
export function registerSecureStorage(storage: SecureStoragePort): void {
  secureStorageInstance = storage
}

/**
 * Obtiene la instancia registrada de SecureStoragePort
 */
export function getSecureStorage(): SecureStoragePort {
  if (!secureStorageInstance) {
    throw new Error('SecureStoragePort no registrado. Llama a registerSecureStorage() primero.')
  }
  return secureStorageInstance
}

/**
 * Verifica si hay un SecureStoragePort registrado
 */
export function isSecureStorageRegistered(): boolean {
  return secureStorageInstance !== null
}

/**
 * Resetea los registros (útil para tests)
 */
export function resetStorage(): void {
  storageInstance = null
  secureStorageInstance = null
}
